import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../../lib/utils';
import { toneSurface } from '../../lib/tone';

/**
 * Badge — one axis, five values.
 *
 * shadcn ships four variants and the handoff adds four status tones, and
 * carrying both meant two ways to say the same thing in one enum: `destructive`
 * beside `danger`, `secondary` beside `neutral`. A caller had no way to tell
 * which set they were meant to reach for. So there is one list now, and the
 * overlapping halves are gone rather than aliased.
 *
 *   default   a filled pill — a count, a label, the thing itself
 *   outline   the same, unfilled, when a badge sits next to other badges
 *   success · warning · danger  what state a row is in
 *
 * The three status surfaces are `lib/tone`'s, the same ones Alert draws itself
 * on. Wash-and-edge rather than solid because they sit in a dense table: a
 * column of solid fills reads as a chart.
 */
const badgeVariants = cva(
  'inline-flex items-center justify-center rounded-chip border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-3 aria-invalid:ring-destructive/20 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden',
  {
    variants: {
      variant: {
        /* The `[a&]` rules only bite once asChild has turned this into a link:
         * a label has nothing to hover. */
        default: 'border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary/90',
        outline: 'text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground',

        success: toneSurface.success,
        warning: toneSurface.warning,
        danger: toneSurface.danger,
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

function Badge({
  className,
  variant,
  asChild = false,
  ...props
}: React.ComponentProps<'span'> & VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : 'span';

  return (
    <Comp data-slot="badge" className={cn(badgeVariants({ variant }), className)} {...props} />
  );
}

export { Badge, badgeVariants };
